/**
 * A catalogue kind, on the shared list engine.
 *
 * Divisions, labor, deployment, rentals, services and measurement types differ
 * only in their columns and their form, so they are one component described
 * six times rather than six files that drift apart. What they do *not* differ
 * in is how the list behaves: the same Fields menu, filters, groups, Σ, saved
 * views, export and import as Contacts and Jobs, because it is literally the
 * same `ListScreen` (plan §3.5).
 *
 * The page bar belongs to the catalogue's own tabs, so the list runs in
 * `embedded` chrome: saved views become chips and the New button sits under
 * the tab instead of in the bar.
 */
import {
  useCallback, useEffect, useState,
  type ChangeEvent, type KeyboardEvent, type ReactNode,
} from 'react'
import { ZONE_COLOURS } from '@rowted/shared'
import { ListScreen, type ListScreenProps } from './list-screen.js'
import { Band, Button, Tag } from './primitives.js'
import { ConfirmDialog, Toast } from './overlays.js'
import { Sheet } from './sheet.js'
import {
  ChipsField, Field, IconPicker, MarkPreview, MoneyInput, NumberInput, SelectInput, SwatchField,
  SwitchRow, TextArea, TextInput,
} from './form/index.js'
import { MARK_COLOUR_NAMES, MARK_PALETTE } from '@rowted/shared'
import type { GlyphSet } from '../frame/mark-glyphs.js'
import type { TileKind } from '../builder/kind-tile.js'
import { useList, type ListArea, type ListRow } from '../lib/use-list.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { IconCheck, IconPlus } from '../frame/icons.js'

export interface CatalogFormField {
  name: string
  label: string
  kind?: 'text' | 'textarea' | 'number' | 'money' | 'percent' | 'select' | 'switch'
    | 'divisions' | 'colour' | 'mark-colour' | 'icon' | 'preview'
  /** Which glyph set an `icon` field picks from, and what a `preview` draws. */
  set?: GlyphSet
  /** For `preview`: the fields it reads, so it follows the two above it. */
  previewOf?: { icon: string; colour: string }
  options?: { value: string; label: string }[]
  placeholder?: string
  hint?: string
  required?: boolean
  /** Shown beside the label: "per crew hour", "%" and so on. */
  suffix?: string
  /** Hides the field unless the form says so — a markup beside a flat rate. */
  when?: (form: Record<string, unknown>) => boolean
  /**
   * Other fields this one suggests a value for when it changes — and **only**
   * while they still hold the value it suggested last time.
   *
   * Board 4 asks for exactly this on the equipment type's Family: changing it
   * re-suggests the icon and the colour "unless the person already picked one".
   * Without the second half there are only two bad options — a suggestion that
   * never moves, or a choice that gets silently overwritten — so the rule is
   * the feature, not a nicety.
   */
  suggests?: Record<string, (value: string) => string>
}

export interface CatalogListProps {
  /** The list area, which is also the path under /api/v1. */
  area: ListArea
  title: string
  /**
   * The tile on the flyout's header — the same one the tab's rows wear, so
   * the picture does not change between the list and the form.
   */
  kind?: TileKind
  /** Singular, for the New button and the form's heading. */
  singular: string
  canEdit: boolean
  /** Defaults for a new row, keyed the way the API expects. */
  blank: Record<string, unknown>
  fields: CatalogFormField[]
  /** Per-column renderers, for columns that are more than formatted text. */
  cell?: Record<string, (row: ListRow) => ReactNode>
  /** The bold primary line and its muted second line. */
  primary: (row: ListRow) => { primary: ReactNode; secondary?: ReactNode }
  /**
   * The first column's header, where it is not the list's title — *Lead
   * source* over a list called Lead sources (Board 12 D).
   */
  primaryHeader?: string
  /**
   * The little square before the name — a 24px `KindTile` on Packages and
   * Services (`packages-catalog.html`).
   *
   * A catalog tab is seven tables that look alike at a glance, and the tile
   * is what tells you which one you are in without reading the tab strip
   * again. It is the same tile those rows wear inside the quote builder, so
   * the picture does not change on the way from the catalog into a quote.
   */
  mark?: { width?: number; render: (row: ListRow) => ReactNode }
  /** Columns a `mark` or a `cell` reads that the view may not be showing. */
  alsoFetch?: string[]
  emptyTitle: string
  emptyBody: string
  /** Bumped by the parent when something the list depends on changed. */
  reloadKey?: number
  onChanged?: () => void
  /**
   * The row as the form wants it, when the two differ — a labor rate stores
   * one of two prices and the form asks which, so the question has to be
   * derived from the answer on the way in and dropped on the way out.
   */
  toForm?: (row: Record<string, unknown>) => Record<string, unknown>
  toBody?: (form: Record<string, unknown>) => Record<string, unknown>
  /**
   * An editor of its own, for a kind a flat form cannot express. A service blends
   * five kinds of line and switches between two ways of pricing, which is a
   * screen, not a field list.
   */
  editor?: (props: { id: string | null; onClose: () => void; onSaved: (m: string) => void })
    => ReactNode
  /**
   * A whole page instead of a form.
   *
   * Given, it takes over from the slide-over: a row and the New button call it
   * with the id, or `'new'`. For a package, which is visits holding services
   * holding lines — a builder, not forty fields in a drawer.
   */
  open?: (id: string | 'new') => void
  /**
   * Where an imported row lands, which is what makes Import appear (§7).
   *
   * Given by every catalog tab now. The tabs had no Import at all, not
   * because a catalog is a bad thing to import — it is the best thing to
   * import — but because `ListScreen` shows the button only when it knows
   * which branch, and nobody had passed it.
   */
  importOrgUnitId?: string
  /**
   * What a row does when it is opened, when that depends on the row — the
   * Equipment tab's owned machines open their own record, the hired-in ones
   * the catalog form (§7). True when it handled the row; false falls
   * through to the editor.
   */
  openRow?: (row: ListRow) => boolean
  /** Where a row is removed, when that depends on the row; null cannot be. */
  removePath?: (row: ListRow) => string | null
  /** The phone card's third line (`list-engine-v3.md` §8). */
  cardLine?: ListScreenProps['cardLine']
}

export function CatalogList({
  area, title, kind = 'service', singular, canEdit, blank, fields, cell, primary, primaryHeader,
  mark, alsoFetch, emptyTitle, emptyBody, reloadKey, onChanged, editor, toForm, toBody, open,
  importOrgUnitId, openRow, removePath, cardLine,
}: CatalogListProps) {
  /*
   * `active` because the row draws its Offered tag from it, and `alwaysFetch`
   * because a tab whose view does not show the column would otherwise get
   * `undefined` and draw every row as offered. The same is true of anything a
   * `mark` reads, so the caller can name its own.
   */
  const list = useList(area, { alwaysFetch: ['active', ...(alsoFetch ?? [])] })
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [removing, setRemoving] = useState<ListRow | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (reloadKey !== undefined) list.reload() }, [reloadKey])

  const openOne = (row: ListRow) => {
    if (openRow?.(row)) return
    if (open) open(String(row.id))
    else if (canEdit) setEditing(row.id)
  }

  const done = (message: string) => {
    setEditing(null)
    setNotice(message)
    list.reload()
    onChanged?.()
  }

  async function remove(row: ListRow) {
    setBusy(true)
    try {
      await request(removePath?.(row) ?? `/${area}/${row.id}`, { method: 'DELETE' })
      setRemoving(null)
      setNotice(`Removed ${String(row.name ?? singular)}.`)
      list.reload()
      onChanged?.()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That could not be removed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ListScreen
        area={area}
        chrome="embedded"
        title={title}
        list={list}
        importOrgUnitId={canEdit ? importOrgUnitId : undefined}
        actions={canEdit
          /* Sentence case, like every other button in the product (§3.8).
             This one title-cased its noun and read "New Labor Rate". */
          ? (
            <Button variant="primary" onClick={() => (open ? open('new') : setEditing('new'))}>
              <IconPlus size={15} />New {singular}
            </Button>
          )
          : undefined}
        primaryColumn={{ field: 'name', header: primaryHeader ?? title, render: primary }}
        mark={mark}
        cell={{
          /*
           * A tag, not a word: whether a thing is still offered is a status,
           * and every other status in the product is drawn as one.
           *
           * "Active" and "Inactive", because that is what the switch on the
           * form now says. It read "Offered"/"Retired", which described the
           * consequence rather than the setting and left the list and the
           * form using two vocabularies for one boolean.
           */
          active: (row) => (row.active === false
            ? <Tag>Inactive</Tag>
            : <Tag tone="ok">Active</Tag>),
          ...cell,
        }}
        /*
         * The engine's ⋯ (Board 3), not a × of the catalog's own: Open, and
         * Remove under a rule. A row that cannot be removed here — an owned
         * machine is retired on its own record — has no Remove.
         */
        rowMenu={(row) => [
          { key: 'open', label: 'Open', onSelect: () => openOne(row) },
          ...(canEdit && (removePath ? removePath(row) !== null : true)
            ? [{
              key: 'remove', label: 'Remove', tone: 'danger' as const, separatorBefore: true,
              onSelect: () => setRemoving(row),
            }]
            : []),
        ]}
        cardLine={cardLine}
        onRowClick={openRow || open || canEdit ? (row) => openOne(row) : undefined}
        emptyTitle={emptyTitle}
        emptyBody={emptyBody}
        emptyAction={canEdit
          ? { label: `New ${singular}`, onClick: () => (open ? open('new') : setEditing('new')) }
          : undefined}
      />

      {editing && (editor
        ? editor({
          id: editing === 'new' ? null : editing,
          onClose: () => setEditing(null),
          onSaved: done,
        })
        : (
          <CatalogForm
            area={area}
            id={editing === 'new' ? null : editing}
            kind={kind}
            singular={singular}
            blank={blank}
            fields={fields}
            toForm={toForm}
            toBody={toBody}
            onClose={() => setEditing(null)}
            onSaved={done}
          />
        ))}

      {removing && (
        <ConfirmDialog
          title={`Remove ${String(removing.name ?? singular)}?`}
          busy={busy}
          body={
            <p className="m-0 text-body text-text-muted">
              It stops being offered. Anything already priced with it keeps working, because
              nothing here is ever really deleted.
            </p>
          }
          onCancel={() => setRemoving(null)}
          onConfirm={() => void remove(removing)}
        />
      )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </>
  )
}

/**
 * The flyout that creates or edits one row — `docs/record-flyouts.md` §5,
 * 480 on the `Sheet`.
 *
 * A catalog row is a handful of named answers, so unlike the material or
 * equipment forms it is not laid out by hand: the tab hands over a list of
 * `CatalogFormField`s and this draws them, one `Field` each, in one column.
 * That is why the whole catalog looks like one form rather than seven.
 *
 * What went with the migration: the page-local `Select` (a bare `<select>`),
 * the hand-rolled `<label><span>` pairs, the red asterisk, and the footer
 * that printed its error in 11px beside the buttons. Errors are on the field
 * that has them; the footer says how many.
 */
function CatalogForm({
  area, id, kind, singular, blank, fields, toForm, toBody, onClose, onSaved,
}: {
  area: string
  id: string | null
  kind: TileKind
  singular: string
  blank: Record<string, unknown>
  fields: CatalogFormField[]
  toForm?: (row: Record<string, unknown>) => Record<string, unknown>
  toBody?: (form: Record<string, unknown>) => Record<string, unknown>
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [form, setForm] = useState<Record<string, unknown> | null>(id ? null : { ...blank })
  const [saved, setSaved] = useState<Record<string, unknown> | null>(id ? null : { ...blank })
  const [problem, setProblem] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    try {
      const row = { ...blank, ...(await request<Record<string, unknown>>(`/${area}/${id}`)) }
      const shaped = toForm ? toForm(row) : row
      setForm(shaped)
      setSaved(shaped)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be loaded.')
    }
  }, [area, id])

  useEffect(() => { void load() }, [load])

  const shown = form
    ? fields.filter((field) => !field.when || field.when(form))
    : []

  const changes = form && saved
    ? Object.keys({ ...saved, ...form })
      .filter((key) => JSON.stringify(form[key]) !== JSON.stringify(saved[key])).length
    : 0

  async function save(again: boolean) {
    if (!form) return

    /* Only what is on screen: a hidden field cannot be filled in. */
    const found: Record<string, string> = {}
    for (const field of shown) {
      if (!field.required) continue
      const value = form[field.name]
      const empty = value === null || value === undefined || value === ''
      if (empty) found[field.name] = `${field.label} is needed`
    }
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setProblem(null)
    try {
      const body = toBody ? toBody(form) : { ...form }
      delete body.id
      await request(id ? `/${area}/${id}` : `/${area}`, { method: id ? 'PUT' : 'POST', body })
      const message = id ? 'Saved.' : `Added ${String(form.name ?? singular)}.`

      if (again && !id) {
        /*
         * A fresh row, not the one just written. Everything a catalog row
         * holds is what makes it that row, so there is nothing to carry —
         * what "add another" saves is the trip back to the New button.
         */
        setForm({ ...blank })
        setSaved({ ...blank })
        onSaved(message)
        return
      }
      onSaved(message)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const count = Object.keys(errors).length

  return (
    <Sheet
      open
      onClose={onClose}
      kind={kind}
      width={480}
      title={id ? String(form?.name ?? singular) : `New ${singular}`}
      sub={`Catalog · ${singular}`}
      dirty={changes > 0}
      changeCount={changes}
      saveLabel={id ? 'Save' : `Add ${singular}`}
      onSave={() => void save(false)}
      note={count > 0
        ? (
          <span className="text-danger">
            {count === 1 ? 'One field needs attention' : `${count} fields need attention`}
          </span>
        )
        : undefined}
      secondary={!id
        ? (
          <Button className="h-[34px]" disabled={busy || !form} onClick={() => void save(true)}>
            Save &amp; add another
          </Button>
        )
        : undefined}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy || !form}
          onClick={() => void save(false)}
        >
          <IconCheck size={15} />
          {busy ? 'Saving…' : id ? 'Save' : `Add ${singular}`}
        </Button>
      )}
    >
      {problem && <Band tone="amber">{problem}</Band>}

      {!form
        ? <p className="m-0 text-[13px] text-text-muted">Loading…</p>
        : shown.map((field) => (
          field.kind === 'divisions'
            ? (
              /*
               * This one field is two answers — "everywhere?" and "which?"
               * — so it reads and writes both keys rather than one.
               */
              <CatalogInput
                key={field.name}
                field={field}
                value={{
                  all: form.allDivisions === true,
                  ids: (form.divisionIds as string[] | undefined) ?? [],
                }}
                onChange={(value) => {
                  const next = value as { all: boolean; ids: string[] }
                  setForm({ ...form, allDivisions: next.all, divisionIds: next.ids })
                }}
              />
            )
            : (
              <CatalogInput
                key={field.name}
                field={field}
                error={errors[field.name]}
                value={form[field.name]}
                form={form}
                onChange={(value) => {
                  setForm({ ...form, [field.name]: value, ...suggestionsFor(field, form, value) })
                  setErrors((current) => Object.fromEntries(
                    Object.entries(current).filter(([key]) => key !== field.name)))
                }}
              />
            )
        ))}
    </Sheet>
  )
}

/**
 * What a field's change suggests for its neighbours — only where the
 * neighbour is untouched.
 *
 * "Untouched" means it still holds what *this* field suggested for its own
 * previous value, or nothing at all. So Family truck → trailer moves the icon
 * from `pickup` to `trailer`, and Family truck → trailer leaves a hand-picked
 * `dump` exactly where it is.
 */
function suggestionsFor(
  field: CatalogFormField, form: Record<string, unknown>, next: unknown,
): Record<string, unknown> {
  if (!field.suggests || typeof next !== 'string') return {}
  const was = typeof form[field.name] === 'string' ? form[field.name] as string : ''
  const out: Record<string, unknown> = {}
  for (const [target, suggest] of Object.entries(field.suggests)) {
    const held = form[target]
    const untouched = !held || held === '' || (was !== '' && held === suggest(was))
    if (untouched) out[target] = suggest(next)
  }
  return out
}

/**
 * One `CatalogFormField`, as the kit draws it.
 *
 * Every branch here used to be its own `<label>` with its own spacing, its
 * own 11px hint and its own idea of what a required marker looks like. Now
 * each is a `Field` wrapping a kit control, so a select in the catalog is the
 * same control as a select on a material.
 */
export function CatalogInput({
  field, value, onChange, error, form = {},
}: {
  field: CatalogFormField
  value: unknown
  onChange: (value: unknown) => void
  error?: string
  /**
   * The whole form, for the two kinds that are about more than one field: an
   * icon tile is tinted by the colour beside it, and the preview is a picture
   * of both. `when` already took it; this hands it to the renderer too.
   */
  form?: Record<string, unknown>
}) {
  if (field.kind === 'switch') {
    return (
      <SwitchRow
        label={field.label}
        sub={field.hint}
        checked={Boolean(value)}
        onChange={onChange}
      />
    )
  }

  if (field.kind === 'divisions') {
    return <DivisionPicker field={field} value={value} onChange={onChange} />
  }

  /*
   * A colour here is stored data — it is drawn on the schedule board, in the
   * crew app and on a PDF, none of which has our CSS variables — so the
   * swatches are hexes and `ZONE_COLOURS` is the one list of them.
   */
  if (field.kind === 'colour') {
    return (
      <Field label={field.label} group hint={field.hint} error={error}>
        {({ labelId }) => (
          <SwatchField
            label={field.label}
            labelledBy={labelId}
            value={(value as string) ?? null}
            onChange={onChange}
            options={ZONE_COLOURS.map((one) => ({
              value: one.value, css: one.value, label: one.label,
            }))}
          />
        )}
      </Field>
    )
  }

  /*
   * The ten the palette allows, and only those (§1). The division colour used
   * to come from `ZONE_COLOURS`, which is a different list for a different job
   * — a zone is a place on a map and a mark is a tint behind a glyph.
   *
   * A division saved from that old list has a colour that is not among these
   * ten, so **nothing is ticked** and the required field makes picking one the
   * price of the next edit — the import's rule, where a change is a visible
   * fix rather than a silent write. The preview beside it still draws the
   * colour the row actually holds (`markInk` tolerates any hex on purpose), so
   * the panel shows the mark as it is today next to the ten it could become.
   * Recolouring those rows in a migration was the alternative, and a colour
   * nobody asked us to change is not ours to change.
   */
  if (field.kind === 'mark-colour') {
    return (
      <Field label={field.label} required={field.required} group hint={field.hint} error={error}>
        {({ labelId }) => (
          <SwatchField
            label={field.label}
            labelledBy={labelId}
            value={(value as string) ?? null}
            onChange={onChange}
            shape="circle"
            options={MARK_COLOUR_NAMES.map((name) => ({
              value: MARK_PALETTE[name], css: MARK_PALETTE[name], label: name,
            }))}
          />
        )}
      </Field>
    )
  }

  if (field.kind === 'icon') {
    return (
      <Field label={field.label} required={field.required} group hint={field.hint} error={error}>
        {({ labelId }) => (
          <IconPicker
            set={field.set ?? 'division'}
            label={field.label}
            labelledBy={labelId}
            value={(value as string) ?? null}
            onChange={onChange}
            colour={(form[field.previewOf?.colour ?? 'colour'] as string) ?? null}
          />
        )}
      </Field>
    )
  }

  /* Not a field — a picture of the two above it, which is the whole point of
     picking an icon beside a colour rather than one and then the other. */
  if (field.kind === 'preview') {
    return (
      <MarkPreview
        set={field.set ?? 'division'}
        slug={(form[field.previewOf?.icon ?? 'icon'] as string) ?? null}
        colour={(form[field.previewOf?.colour ?? 'colour'] as string) ?? null}
      />
    )
  }

  if (field.kind === 'select') {
    return (
      <Field label={field.label} required={field.required} hint={field.hint} error={error}>
        {({ id, describedBy, invalid }) => (
          <SelectInput
            id={id}
            describedBy={describedBy}
            invalid={invalid}
            label={field.label}
            value={(value as string) || null}
            placeholder={field.placeholder ?? (field.required ? 'Choose…' : 'None')}
            clearable={!field.required}
            clearLabel={field.placeholder ?? 'None'}
            onChange={(next) => onChange(next)}
            options={field.options ?? []}
          />
        )}
      </Field>
    )
  }

  if (field.kind === 'textarea') {
    return (
      <Field label={field.label} hint={field.hint} error={error}>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            describedBy={describedBy}
            invalid={invalid}
            rows={3}
            value={(value as string) ?? ''}
            placeholder={field.placeholder}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </Field>
    )
  }

  if (field.kind === 'number' || field.kind === 'money' || field.kind === 'percent') {
    return <NumberField field={field} value={value} onChange={onChange} error={error} />
  }

  return (
    <Field label={field.label} required={field.required} hint={field.hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <TextInput
          id={id}
          describedBy={describedBy}
          invalid={invalid}
          value={value === null || value === undefined ? '' : String(value)}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  )
}

/**
 * A number you can actually type.
 *
 * Converting on every keystroke means "145." becomes 145 the moment the point
 * is typed, the input re-renders without it, and a decimal rate is
 * unreachable. So the text is local while it is being typed and only becomes
 * a number on blur — which is also the only moment the value is worth
 * validating.
 *
 * That is the behaviour, and it is why this is a component rather than a
 * `NumberInput` in the branch above: the kit's number box holds whatever
 * string it is given, and the string has to survive the round trip.
 */
function NumberField({
  field, value, onChange, error,
}: {
  field: CatalogFormField
  value: unknown
  onChange: (value: unknown) => void
  error?: string
}) {
  /*
   * A `numeric` column arrives as "62.5000". Showing the trailing zeros in an
   * input somebody is about to edit is noise, so it is normalised on the way
   * in — and left exactly as typed while they are typing.
   */
  const asText = (input: unknown) => {
    if (input === null || input === undefined || input === '') return ''
    const number = Number(input)
    return Number.isFinite(number) ? String(number) : String(input)
  }

  const [text, setText] = useState(asText(value))
  const [focused, setFocused] = useState(false)

  // A save that reshapes the row — a markup filling in a billed rate — should
  // reach the box, but not while somebody is mid-number in it.
  useEffect(() => { if (!focused) setText(asText(value)) }, [value, focused])

  const commit = () => {
    setFocused(false)
    const cleaned = text.replace(/[^0-9.-]/g, '')
    if (cleaned === '' || cleaned === '-' || cleaned === '.') {
      onChange(null)
      setText('')
      return
    }
    const next = Number(cleaned)
    if (!Number.isFinite(next)) {
      onChange(null)
      setText('')
      return
    }
    onChange(next)
    setText(String(next))
  }

  const common = {
    value: text,
    placeholder: field.placeholder,
    onFocus: () => setFocused(true),
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value
      setText(next)
      // Push anything that already parses, so a live total moves as the
      // number is typed. "145." parses to 145 and the text keeps its
      // point, which is the whole trick.
      const cleaned = next.replace(/[^0-9.-]/g, '')
      if (cleaned !== '' && Number.isFinite(Number(cleaned))) onChange(Number(cleaned))
    },
    onBlur: commit,
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') event.currentTarget.blur()
    },
  }

  return (
    <Field
      label={field.label}
      required={field.required}
      hint={field.hint}
      error={error}
    >
      {({ id, describedBy, invalid }) => (
        field.kind === 'money'
          ? (
            <MoneyInput
              {...common}
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              unit={field.suffix}
            />
          )
          : (
            <NumberInput
              {...common}
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              percent={field.kind === 'percent'}
              unit={field.kind === 'percent' ? undefined : field.suffix}
            />
          )
      )}
    </Field>
  )
}

/**
 * All divisions, some, one or none.
 *
 * Four answers, so a checkbox list alone will not do: an empty list has to
 * mean "none" rather than "everywhere", and "everywhere" has to keep meaning
 * everywhere when somebody adds a division next month. "All" is its own
 * switch for exactly that reason.
 *
 * The chips are `ChipsField` now rather than a hand-rolled pill row — which
 * is what `record-flyouts.md` §5 means by "`DivisionPicker` → `ChipsField`":
 * the two-answer logic stays, the drawing is the kit's.
 */
function DivisionPicker({
  field, value, onChange,
}: {
  field: CatalogFormField
  value: unknown
  onChange: (value: unknown) => void
}) {
  // The picker owns two keys, so it is handed the whole form slice rather
  // than one value.
  const state = (value as { all: boolean; ids: string[] } | undefined)
    ?? { all: false, ids: [] }

  return (
    <Field
      group
      label={field.label}
      hint={state.all
        ? 'Offered on every division, including ones added later.'
        : state.ids.length === 0
          ? 'No division yet — it is still offered, it just sorts under nothing.'
          : `Offered on ${state.ids.length} division${state.ids.length === 1 ? '' : 's'}.`}
    >
      {({ labelId }) => (
        <div className="flex flex-col gap-2">
          <SwitchRow
            label="Every division"
            checked={state.all}
            onChange={(next) => onChange({ all: next, ids: next ? [] : state.ids })}
          />

          <div className={cn(state.all && 'pointer-events-none opacity-40')}>
            {(field.options ?? []).length === 0
              ? <span className="text-[11px] text-text-faint">No divisions yet.</span>
              : (
                <ChipsField
                  label={field.label}
                  labelledBy={labelId}
                  options={field.options ?? []}
                  value={state.ids}
                  onChange={(ids) => onChange({ all: false, ids })}
                />
              )}
          </div>
        </div>
      )}
    </Field>
  )
}
